import styled from 'styled-components'
import { PointIcon } from '../../../../assets/icons/point'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ResizeIcon } from '../../../../assets/icons/resize'
import _ from 'underscore'
import AboutBiomarker, {
  Infinity,
  getSignalColorsByLetterLocalized,
  signalColorsByLetter,
  UnitType,
} from './AboutBiomarker'
import { DialogMUI } from '../sharedStyles'

const ContainerBiomarker = styled.div<{ bgColor: string; isGrey?: boolean }>`
  cursor: pointer;
  display: flex;
  opacity: ${({ isGrey }) => (isGrey ? 0.1 : 1)};
  flex-direction: column;
  padding:12px 16px 16px 16px;
  margin-bottom: 0px;
  min-height: 100px;
  border-radius: 8px;
  border: 1px solid var(--border-main, #ededed);
  background: var(--global-white, #f7f7f7);
  transition: all .4s ease;

  &:hover {
    transition: all .4s ease;
  ${({ bgColor }) =>
    bgColor &&
    `background: linear-gradient(175deg, ${bgColor}, 50%, #fff, #fff)`};
    box-shadow: 0px 0px 7px 4px #afafaf2e;
  }
  
  @media print {
    &:hover {
      background: #f5f5f5!important;
      box-shadow: none!important;
    }
  }

  @media (max-width: 650px) {
  }
`
const Title = styled.div`
  margin-bottom: 30px;
`

const LineColor = styled.div<{
  selected: boolean
  color: string
  width: string
  isBig?: boolean
}>`
  position: relative;
  height: ${({ selected, isBig }) => (isBig ? selected ? '10px' : '4px' : selected ? '6px' : '2px')};
  opacity: ${({ selected, isBig }) => (selected ? '1' : isBig ? '1' : '.4')};
  width: ${({ width }) => width};

  svg {
    width: 101%;
    height: 100%;
    position: absolute;
  }
`

const LinesContainer = styled.div`
  margin-top: 20px;
  width: 100%;
  display: flex;
  align-items: flex-end;
  position: relative;
`

const PointContainer = styled.div<{ color: string; left: string, isBig?: boolean }>`
  position: absolute;
  bottom: ${({ isBig }) => isBig ? '5px' : '0'};;
  left: ${({ left }) => left};
  margin-left: -4px;

  svg path {
    fill: ${({ color }) => color};
  }
`

const Description = styled.div`
  margin-top: 10px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  p {
    margin-right: 10px;
    color: var(--text-secondary, #808080);
    text-align: right;
    text-overflow: ellipsis;
    font-family: Roboto;
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    letter-spacing: 0.24px;
  }

  span {
    color: var(--text-main, #1a1a1a);
    text-overflow: ellipsis;
    font-family: Roboto;
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    letter-spacing: 0.24px;
  }
`

const Gradient = styled.div`
  background: linear-gradient(
    0deg,
    rgba(255, 255, 255, 1) 0%,
    rgba(252, 209, 0, 0.8113839285714286) 50%,
    rgba(255, 255, 255, 1) 100%
  );
`

const Head = styled.div`
  position: relative;

  svg {
    cursor: pointer;
    position: absolute;
    top: 0px;
    right: 0px;
  }
`

const MeasuredBiomarkers = ({
  onClickContainer,
  countLine = 5,
  onClickResize,
  indicator,
  value,
  isGrey,
}: {
  onClickResize?: () => void
  onClickContainer?: () => void
  countLine?: number
  indicator?: any
  isGrey?: boolean
  value?: number
}) => {
  const { i18n } = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)

  indicator ??= {}

  function onClickIndicator() {
    setIsModalOpen(true)
  }

  function handleCloseModal() {
    setIsModalOpen(false)
  }

  let colors: any = {
    R: '#E9686310',
    G: '#C2EAED50',
    A: '#FFECB330',
    GL: '#DEF7EE50',
  }

  const getLocalizedText = (text: any): string => {
    if (!text) return '';
    if (typeof text === 'string') return text;
    if (typeof text === 'object') {
      const currentLanguage = i18n.language;
      const langCode = (currentLanguage || '').split('-')[0];
      return text[currentLanguage] || text[langCode] || text.en || text.de || (Object.values(text)[0] as string) || '';
    }
    return '';
  };

  const indicatorName = getLocalizedText(indicator.name) || `Biomarker #${indicator._id}`;

  return (
    <>
      <DialogMUI
        scroll='body'
        aria-labelledby='scroll-dialog-title'
        aria-describedby='scroll-dialog-description'
        onClose={handleCloseModal}
        open={isModalOpen}
        data-modal-mui
      >
        <AboutBiomarker
          onClose={handleCloseModal}
          indicator={indicator}
          value={value}
        />
      </DialogMUI>
      <span>
      <ContainerBiomarker
        bgColor={colors[indicator.scaleValue]}
        className='container-biomarker linkHover transform'
        onClick={onClickIndicator}
      >
        <div className={indicator.scaleValue == 'GREY' ? 'nonActive' : ''}>
          <Head>
            <Title>
              {indicatorName}
            </Title>
            <ResizeIcon onClick={onClickResize} data-resize />
          </Head>
          <BiomarkerLines indicator={indicator} />
          <Description data-desc4>
            <span>{value || '0'}</span>
          </Description>
        </div>
      </ContainerBiomarker>
        </span>
    </>
  )
}

export const getIndPerc = (indicator: any) => {
  const { _value, _values } = indicator;
  
  if (!_values || !Array.isArray(_values) || _values.length === 0) {
    return 50;
  }
  
  const value = Number(_value);
  if (isNaN(value)) {
    return 50;
  }
  
  let currentRange = { index: 0, from: _values[0].valueFrom, to: _values[0].valueTo };
  
  _values.forEach((signal: any, index: number) => {
    if (value >= signal.valueFrom) {
      currentRange = {
        index,
        from: signal.valueFrom,
        to: signal.valueTo
      };
    }
  });
  
  const segmentWidth = 100 / _values.length;
  const rangeDelta = currentRange.to - currentRange.from;
  
  if (rangeDelta <= 0) {
    return segmentWidth * currentRange.index + segmentWidth / 2;
  }
  
  const valuePosition = Math.max(0, Math.min(1, (value - currentRange.from) / rangeDelta));
  const percentage = segmentWidth * currentRange.index + valuePosition * segmentWidth;
  
  return Math.max(1, Math.min(99, percentage));
}

export const getSignalColorsByLetter = (letter:any, _letter: any) => {
  return signalColorsByLetter[_letter] || signalColorsByLetter[letter] || signalColorsByLetter['GREY'] || {}
}

export const getSignalColorsByLetterWithTranslation = (letter:any, _letter: any, t: any) => {
  const signalColors = getSignalColorsByLetterLocalized(t)
  return signalColors[_letter as keyof typeof signalColors] || signalColors[letter as keyof typeof signalColors] || signalColors['GREY'] || {}
}

export const TO_FIXED_DEF = 3;

const getLocalizedName = (name: any): string => {
  if (!name) return '';
  if (typeof name === 'string') return name;
  if (typeof name === 'object') {
    return name.en || name.de || (Object.values(name)[0] as string) || '';
  }
  return '';
};

export const BiomarkerLines = ({ indicator, isBig }: { indicator: any, isBig?: boolean }) => {
  const { t } = useTranslation()
  const _values = indicator._values || [];
  const isGrey = indicator.scaleValue === 'GREY';
  const pointColor = getSignalColorsByLetterWithTranslation(indicator.scaleValue, indicator._scaleValue, t);
  const indicatorNameStr = getLocalizedName(indicator.name);
  
  const percentage = getIndPerc(indicator);
  
  const formatValue = (value: any) => {
    return +(+value || 0).toFixed(TO_FIXED_DEF);
  };
  
  const segmentWidth = 100 / (_values.length || 1);
  const infinityIcon = <Infinity color={'#808080'} top={5} />;
  
  return (
    <>
      <LinesContainer>
        {!isGrey && (
          <PointContainer
            color={pointColor.color}
            left={percentage + '%'}
            isBig={isBig}
          >
            <PointIcon />
          </PointContainer>
        )}
        
        {_values.map((signal: any, index: number) => {
          const signalColor = getSignalColorsByLetterWithTranslation(signal.scaleValue, null, t);
          const isSelected = !isGrey && indicator.selectedInd === index;
          const isLastSegment = index + 1 === _values.length;
          
          return (
            <LineColor
              data-line
              isBig={isBig}
              width={segmentWidth + '%'}
              key={`${signal.color}-${index}`}
              selected={isSelected}
              color={signalColor.color}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className='line'>
                <rect width="100%" height="100%" fill={signalColor.color || 'grey'} />
              </svg>
              
              {isBig && (
                <>
                  <div className="indicatorValueStart indicatorValue">
                    {index > 0 ? formatValue(signal.valueFrom) : (indicatorNameStr === 'Eosinophils' || indicatorNameStr === 'Basophils' || indicatorNameStr === 'Total cholesterol' || indicatorNameStr === 'Triglycerides') ? '0' : <span>{infinityIcon}</span>}
                  </div>
                  {isLastSegment && (
                    <div className="indicatorValueEnd indicatorValue">
                      <span>{infinityIcon}</span>
                    </div>
                  )}
                </>
              )}
            </LineColor>
          );
        })}
      </LinesContainer>
    </>
  );
};

export default MeasuredBiomarkers
